import React from 'react';
import { Message, Icon, Label } from 'semantic-ui-react';

/**
 * Active Test Context Display Component
 * Shows the current test scenario context with selected participants
 */
const ActiveTestContext = ({
  selectedVoiceActor,
  selectedBookCreator,
  selectedAdmin,
  isAdminOverride,
  voiceActors,
  bookCreators,
  admins,
  selectedScenario,
  businessScenarios
}) => {
  // Get user objects
  const voiceActor = selectedVoiceActor ? voiceActors[selectedVoiceActor] : null;
  const bookCreator = selectedBookCreator ? bookCreators[selectedBookCreator] : null;
  const admin = selectedAdmin ? admins[selectedAdmin] : null;
  const scenario = selectedScenario ? businessScenarios.find(s => s.id === selectedScenario) : null;

  // Determine active participants and scenario direction
  const getScenarioDirection = () => {
    if (!scenario) return null;

    if (isAdminOverride && admin) {
      return {
        sender: admin,
        recipient: scenario.scenario.sender === 'VOICE_ACTOR' ? voiceActor : bookCreator,
        direction: `${admin.name} (Admin Override) → ${scenario.scenario.sender === 'VOICE_ACTOR' ? voiceActor?.name : bookCreator?.name}`,
        context: `Admin testing ${scenario.name}`
      };
    }

    if (scenario.scenario.sender === 'VOICE_ACTOR' && scenario.scenario.recipient === 'BOOK_CREATOR') {
      return {
        sender: voiceActor,
        recipient: bookCreator,
        direction: `${voiceActor?.name} → ${bookCreator?.name}`,
        context: scenario.name
      };
    }

    if (scenario.scenario.sender === 'BOOK_CREATOR' && scenario.scenario.recipient === 'VOICE_ACTOR') {
      return {
        sender: bookCreator,
        recipient: voiceActor,
        direction: `${bookCreator?.name} → ${voiceActor?.name}`,
        context: scenario.name
      };
    }

    return null;
  };

  const scenarioDirection = getScenarioDirection();

  // Don't show if no participants selected
  if (!voiceActor && !bookCreator && !admin) {
    return null;
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <Message info>
        <Message.Header>
          <Icon name="users" />
          Active Test Context
          {isAdminOverride && (
            <Label color="orange" size="small" style={{ marginLeft: '10px' }}>
              <Icon name="warning sign" />
              Admin Override Mode
            </Label>
          )}
        </Message.Header>

        <div style={{ marginTop: '10px' }}>
          {/* Selected Participants */}
          <div style={{ marginBottom: '8px' }}>
            <strong>Selected Participants:</strong>
          </div>

          <div style={{ display: 'flex', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
            {voiceActor && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#e8f4fd',
                borderRadius: '4px',
                border: '1px solid #bee5eb'
              }}>
                <Icon name="microphone" color="blue" />
                <strong>Voice Actor:</strong> {voiceActor.name}
                {voiceActor.isRealUser && (
                  <Label size="mini" color="green" style={{ marginLeft: '5px' }}>Real</Label>
                )}
              </div>
            )}

            {bookCreator && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <Icon name="book" color="grey" />
                <strong>Book Creator:</strong> {bookCreator.name}
                {bookCreator.isRealUser && (
                  <Label size="mini" color="green" style={{ marginLeft: '5px' }}>Real</Label>
                )}
              </div>
            )}

            {admin && isAdminOverride && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#fff3cd',
                borderRadius: '4px',
                border: '1px solid #ffeaa7'
              }}>
                <Icon name="shield" color="orange" />
                <strong>Admin Override:</strong> {admin.name}
                {admin.isRealUser && (
                  <Label size="mini" color="green" style={{ marginLeft: '5px' }}>Real</Label>
                )}
              </div>
            )}
          </div>

          {/* Scenario Direction */}
          {scenarioDirection && (
            <div style={{
              padding: '10px',
              backgroundColor: '#f0f8ff',
              borderRadius: '4px',
              border: '1px solid #b3d9ff'
            }}>
              <div style={{ marginBottom: '5px' }}>
                <strong>Active Scenario:</strong> {scenarioDirection.context}
              </div>
              <div style={{ fontSize: '1.1em', color: '#0066cc' }}>
                <Icon name="arrow right" />
                {scenarioDirection.direction}
              </div>
              {scenario && (
                <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                  {scenario.description}
                </div>
              )}
            </div>
          )}

          {/* Missing Participants Warning */}
          {(!voiceActor || !bookCreator) && !isAdminOverride && (
            <Message warning size="small" style={{ marginTop: '10px' }}>
              <Message.Header>Incomplete Test Setup</Message.Header>
              <p>
                {!voiceActor && !bookCreator && "Please select both a Voice Actor and Book Creator to enable scenario testing."}
                {!voiceActor && bookCreator && "Please select a Voice Actor to complete the test setup."}
                {voiceActor && !bookCreator && "Please select a Book Creator to complete the test setup."}
              </p>
            </Message>
          )}

          {/* Admin Override Info */}
          {isAdminOverride && admin && (
            <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#856404' }}>
              <Icon name="info circle" />
              <strong>Admin Override Active:</strong> Can test any scenario combination and bypass normal role restrictions.
            </div>
          )}
        </div>
      </Message>
    </div>
  );
};

export default ActiveTestContext;
